import {
  age_filter_id as base_age_filter_id,
  approval_rate_id as base_approval_rate_id,
  country_filter_id as base_country_filter_id,
  gender_filter_id as base_gender_filter_id,
} from "../constants";
import {
  BaseFilterSelected,
  BaseRangeFilterSelected,
  BaseSelectFilterSelected,
  PanelRecruitment,
  TargetGroupId,
  TargetGroupWithEntryLink,
  TargetGroupWithRestricted,
} from "../types";
import * as UserInterviews from "./constants";
import {
  age_filter_id,
  BASE_COMPENSATION_CONSUMER_PER_HOUR_CENTS,
  BASE_COMPENSATION_PROFESSIONAL_PER_HOUR_CENTS,
  country_filter_id,
  gender_filter_id,
  SCREEN_MULTIPLIER,
  VIDEO_MULTIPLIER,
} from "./constants";
import { convertStudyScreener } from "./screeners/utils";
import {
  CharacteristicTypeValues,
  DeviceRequirement,
  ParticipantId,
  ParticipantResponse,
  ParticipantStatus,
  RecruitAttributes,
  RecruitCriteria,
} from "./types";
import { getTargetGroupCountryPanelCodes } from "@/components/outreach/panel/countries/targetGroupCountries";
import { RecruitedResponseAction } from "@/components/outreach/panel/tracking/types";
import { QualityScoreCategory } from "@/components/responses/evaluation/quality/types";
import { buildInterviewUrl } from "@/lib/interview/links";
import { notNull, params_to_url_string, recordToArray } from "@/utils/helpers";
import { get_latest_rev } from "@/utils/survey";
import {
  SearchParamsSync,
  SurveyConfig,
  SurveyWithRevisions,
} from "@/utils/types";
import { v5 as uuidv5 } from "uuid";

export const is_user_interview_participant = (
  searchParams: SearchParamsSync
) => {
  const iid = get_participant_id_from_response({ data: { searchParams } });
  return (
    // iid is a string that starts with "pp_" and is 10 characters long, avoid users tampering with params
    notNull(iid) &&
    !Array.isArray(iid) &&
    notNull(iid.match(/^pp_[a-zA-Z0-9]+$/))
  );
};

export const get_participant_id_from_response = (response: {
  data: { searchParams: SearchParamsSync };
}) => {
  const iid = (response.data.searchParams[UserInterviews.UI_ID_PARAM] ||
    response.data.searchParams["IID"]) as ParticipantId | undefined;
  if (Array.isArray(iid)) {
    return null;
  }
  return iid;
};

export const get_target_group_id_from_response = (response: {
  data: { searchParams: SearchParamsSync };
}): TargetGroupId | null => {
  return (
    (response.data.searchParams["__target_group_id"] as
      | TargetGroupId
      | null
      | undefined) ?? null
  );
};

/** Professional (B2B) audiences target a skill or occupation. */
export const hasProfessionalUserInterviewsFilters = (
  filters: BaseFilterSelected[] | undefined
): boolean =>
  (filters ?? []).some(
    (filter) =>
      [
        UserInterviews.occupation_filter_id,
        UserInterviews.skill_filter_id,
      ].includes(filter.filter_id) &&
      filter.type === "select" &&
      filter.selected_values.length > 0
  );

export const calculateUserInterviewsSessionFeeCents = (
  filters: BaseFilterSelected[] | undefined
): number =>
  hasProfessionalUserInterviewsFilters(filters)
    ? UserInterviews.SESSION_FEE_PROFESSIONAL_CENTS
    : UserInterviews.SESSION_FEE_CONSUMER_CENTS;

export const getCompensationAmountFromRecruitmentOrSurvey = ({
  taskDurationMinutes,
  targetGroup,
  format,
}: {
  taskDurationMinutes: number;
  targetGroup: TargetGroupWithRestricted;
  format: "video" | "audio" | "screen_recording";
}): number => {
  if (notNull(targetGroup.restricted.compensation_amount)) {
    return Math.round(targetGroup.restricted.compensation_amount * 100);
  }
  const isProfessional = hasProfessionalUserInterviewsFilters(
    targetGroup.restricted.data?.filters
  );
  return Math.ceil(
    // divide by 60 to convert to cents per minute
    ((isProfessional
      ? BASE_COMPENSATION_PROFESSIONAL_PER_HOUR_CENTS
      : BASE_COMPENSATION_CONSUMER_PER_HOUR_CENTS) /
      60) *
      taskDurationMinutes *
      (format === "screen_recording"
        ? SCREEN_MULTIPLIER
        : format === "video"
          ? VIDEO_MULTIPLIER
          : 1)
  );
};

export const convertStudyToRecruit = ({
  survey,
  recruitment,
  targetGroup,
  numberOfParticipants,
  taskDurationMinutes,
}: {
  survey: SurveyWithRevisions;
  recruitment: PanelRecruitment;
  targetGroup: TargetGroupWithEntryLink;
  numberOfParticipants: number;
  taskDurationMinutes: number;
}): Omit<
  RecruitAttributes,
  "compensationAmount" | "criteria" | "deviceRequirements" | "status"
> => {
  if (!targetGroup.survey_link) {
    throw new Error(
      `target group ${targetGroup.id} has no entry link; cannot build User Interviews task URL`
    );
  }
  const revision = get_latest_rev(survey);
  const screener = convertStudyScreener(revision.data, targetGroup.id);
  const urlParams = params_to_url_string({
    // include url params denoted in the target group
    ...targetGroup.url_params,
    // Unique ID for the participant and this study
    [UserInterviews.UI_ID_PARAM]: "TRACKINGID",
    // include the target group id in the url params so we can get the target group id to check against
    __target_group_id: targetGroup.id,
  });
  return {
    publicTitle: revision.data.external_title ?? "AI User Interview",
    internalName: revision.title,
    publicDescription: recruitment.description,
    screener,
    requireApproval: false,
    numParticipants: numberOfParticipants,
    taskDetails: {
      // TODO(future): Make deadline dynamic based on how hard we think recruitment will be - now hardcoded to 7 days
      // based on request from ops. https://listen-labs.slack.com/archives/C07M3AN5N5N/p1755028991080939
      deadline: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
      taskUrl: urlParams
        ? `${buildInterviewUrl({ linkId: targetGroup.survey_link.link_id })}?${urlParams}`
        : buildInterviewUrl({ linkId: targetGroup.survey_link.link_id }),
      completionInterval: getCompletionIntervalSeconds(
        taskDurationMinutes * 60
      ),
      taskDuration: taskDurationMinutes,
      timeLimitEnabled: true,
    },
  };
};

export const convertTargetGroupToCriteria = (
  config: SurveyConfig,
  restricted_group: TargetGroupWithRestricted
): {
  deviceRequirements: DeviceRequirement[];
  criteria: RecruitCriteria[];
} => {
  const is_video_interview = config.is_video_interview;
  const device_compatibility_criteria: DeviceRequirement[] =
    restricted_group.device_compatibility.map((device) => {
      switch (device) {
        case "desktop":
          return is_video_interview
            ? "device--computer_with_webcam"
            : "device--computer";
        case "mobile":
          return "device--smartphone" as const;
        case "tablet":
          return "device--tablet" as const;
      }
    });
  const { filters } = restricted_group;
  const country_filter_criteria =
    getTargetGroupCountryPanelCodes(restricted_group);
  if (country_filter_criteria.length === 0) {
    throw new Error(
      `target group ${restricted_group.id} has no country; launch should have been blocked`
    );
  }
  const criteria = filters
    .filter((f) => f.filter_id !== base_approval_rate_id)
    .map((filter) =>
      convert_criteria_to_api_format(filter, country_filter_criteria)
    )
    .filter(notNull);
  return {
    deviceRequirements: device_compatibility_criteria,
    criteria,
  };
};

const userInterviewsCharacteristicTypes = new Set<string>(
  CharacteristicTypeValues
);

export const convert_criteria_to_api_format = (
  filter: BaseFilterSelected,
  country_values?: string[]
): RecruitCriteria | null => {
  if (filter.filter_id === base_age_filter_id) {
    return {
      [age_filter_id]: {
        $gte: (filter as BaseRangeFilterSelected).selected_range.lower,
        $lte: (filter as BaseRangeFilterSelected).selected_range.upper,
      },
    } as RecruitCriteria;
  } else if (filter.filter_id === base_gender_filter_id) {
    return {
      [gender_filter_id]: {
        $in: (filter as BaseSelectFilterSelected).selected_values,
      },
    } as RecruitCriteria;
  } else if (filter.filter_id === base_country_filter_id) {
    return {
      [country_filter_id]: {
        $in: country_values,
      },
    } as RecruitCriteria;
  } else {
    if (!userInterviewsCharacteristicTypes.has(filter.filter_id)) {
      return null;
    }
    return {
      [filter.filter_id]: {
        $in: (filter as BaseSelectFilterSelected).selected_values,
      },
    } as RecruitCriteria;
  }
};

const get_device_requirements_study_type = (
  config: SurveyConfig
): DeviceRequirement[] => {
  if (config.is_screen_recording) {
    const requirements: DeviceRequirement[] = [
      "recording--audio",
      "recording--video",
      "recording--screen",
    ];
    const targetsMobile =
      config.targetPlatforms?.includes("ios") ||
      config.targetPlatforms?.includes("android");
    const targetsDesktop = config.targetPlatforms?.includes("desktop") ?? true;

    if (targetsDesktop && !targetsMobile) {
      requirements.push("device--computer_with_webcam");
    } else if (targetsMobile && !targetsDesktop) {
      requirements.push("device--smartphone");
    }
    // If both, don't add device requirement to allow either
    return requirements;
  } else if (config.is_video_interview) {
    return ["recording--audio", "recording--video"];
  } else {
    return ["recording--audio"];
  }
};

export const get_screening_hash = (
  survey: SurveyWithRevisions,
  targetGroupId: TargetGroupId,
  useDev: boolean = false
): string => {
  const revision = get_latest_rev(survey, useDev);
  const screener_data = convertStudyScreener(revision.data, targetGroupId);
  const questions = screener_data.questions;
  const answers = screener_data.answers;
  // construct a consistently ordered string to hash
  const toHash = recordToArray(questions)
    .sort(([idA], [idB]) => idA.localeCompare(idB))
    .map(([, question]) => {
      return `${question.text}:${question.pick}/${recordToArray(answers)
        .sort(([answerIdA], [answerIdB]) => answerIdA.localeCompare(answerIdB))
        .map(([answerId]) => {
          return `${answers[answerId].qualifyLogic}:${answers[answerId].responseClass}:${answers[answerId].text}`;
        })
        .join(",")}`;
    })
    .join("|");
  const hash = uuidv5(toHash, uuidv5.URL);
  return hash;
};

export const getActionFromParticipantStatus = (
  status: ParticipantStatus
): RecruitedResponseAction => {
  switch (status) {
    case "fraudulent":
      return "fraud";
    // completed in our system means the response is complete, not that it is QC'd and paid
    case "task_submitted":
    case "task_started":
      return "completed";
    // complete in user interivews status means paid, which is approved in our system
    case "complete":
      return "approve";
    case "rejected":
      return "reject_screening";
    case "no_show":
      return "reject_quality";
    case "qualified":
    case "qualified_needs_review":
    case "prequalified":
    case "approved":
    case "unqualified":
    case "unsorted":
      throw new Error(`Unexpected status: ${status}`);
    default: {
      const _exhaustiveCheck: never = status;
      return _exhaustiveCheck;
    }
  }
};

export const getUserInterviewsProviderTarget = (
  targetGroup: TargetGroupWithRestricted
): number => {
  const launchedTarget =
    targetGroup.restricted.data?.total_available_places ??
    targetGroup.total_available_places;
  const providerTarget =
    targetGroup.restricted.user_interviews_data?.data.attributes
      .numParticipants ?? launchedTarget;
  return (
    targetGroup.total_available_places +
    Math.max(0, providerTarget - launchedTarget)
  );
};

export const getStateForParticipant = ({
  participant,
  responseUserInterviewIdsMap,
}: {
  participant: ParticipantResponse;
  responseUserInterviewIdsMap: Map<ParticipantId, QualityScoreCategory | null>;
}):
  | "complete"
  | "invalid_complete"
  | "in_progress"
  | "fraudulent"
  | "rejected"
  | "screened"
  | null => {
  switch (participant.attributes.status) {
    case "complete":
      if (responseUserInterviewIdsMap.has(participant.id)) {
        return "complete";
      }
      // potentially can happen if the participant is hidden AFTER they were approved/auto-approved
      return "invalid_complete";
    case "task_submitted":
      if (
        ["average", "good", "excellent"].includes(
          responseUserInterviewIdsMap.get(participant.id) ?? ""
        )
      ) {
        // if the participant has a non-flagging (fraud/low) quality score, count them as complete
        return "complete";
      }
      return "in_progress";
    case "task_started":
      return "in_progress";
    case "approved":
      // only count approved participants if they have a response in our system, meaning they have started
      if (responseUserInterviewIdsMap.has(participant.id)) {
        return "in_progress";
      }
      return null;
    case "fraudulent":
    case "rejected":
      return "rejected";
    case "no_show":
    case "unqualified":
      return "screened";
    case "qualified":
    case "qualified_needs_review":
    case "prequalified":
    case "unsorted":
    default: {
      const status = participant.attributes.status;
      // TODO: move back to error once UI fixes their status filtering
      // console.error(
      console.log(
        `Unexpected user interviews participant status: ${status}, participant id: ${participant.id}`
      );
      return null;
    }
  }
};

// Since User Interviews uses this to timeout an interviewee's session, add a generous buffer so that interviewees don't
// get locked out if our estimates are off.
export function getCompletionIntervalSeconds(
  timeDurationSeconds: number
): number {
  const completionInterval = timeDurationSeconds * 4;

  // User Interviews specifies a minimum of 1 hour. We specifically want a min of 3 days so
  // that we can fulfill niche recruitments.
  const min = 3 * 60 * 60 * 24; // 3 days
  if (completionInterval < min) return min;

  const max = 7 * 60 * 60 * 24; // 7 days
  if (completionInterval > max) return max;

  return completionInterval;
}
